import { UnitRulesEditor } from './UnitRulesEditor';
import { CraftEditor } from './CraftEditor';
import { CompletionEditor } from './CompletionEditor';
import { QuestionForm } from './QuestionEditor';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ClassSchedule } from './ClassSchedule';
import { api, ApiError, uploadFile, type BuilderUnit, type BuilderQuestion, type UploadedFile } from '../../api';

export function UnitEditor({ mode = 'admin' }: { mode?: 'admin' | 'instructor' }) {
  const base = mode === 'instructor' ? '/instructor/courses' : '/admin/courses';
  const { id, unitId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<BuilderUnit | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setData(await api.get<BuilderUnit>(`/admin/builder/${id}/units/${unitId}`)); }
    catch (e) { setError((e as ApiError).message); }
  }, [id, unitId]);
  useEffect(() => { void load(); }, [load]);

  if (error && !data) return <p className="auth__error">{error}</p>;
  if (!data) return <p className="muted">Loading…</p>;
  const u = data.unit;

  const save = async (body: Record<string, unknown>, message = 'Saved.') => {
    setBusy(true); setError(null); setNote(null);
    try { await api.patch(`/admin/builder/${id}/units/${unitId}`, body); setNote(message); await load(); }
    catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  return (
    <>
      <nav className="crumbs">
        <Link to={base}>Courses</Link> <span aria-hidden>›</span>{' '}
        <Link to={`${base}/${id}/edit`}>Course content</Link> <span aria-hidden>›</span> {u.name}
      </nav>

      <div className="page-head">
        <h1 className="h1">{u.name}{!u.isActive && <span className="tag">switched off</span>}</h1>
        <label className="builder__toggle">
          <input type="checkbox" checked={u.isActive} disabled={busy}
            onChange={() => void save({ isActive: !u.isActive }, u.isActive ? 'Hidden from trainees.' : 'Visible to trainees.')} />
          <span>{u.isActive ? 'visible' : 'hidden'}</span>
        </label>
      </div>

      {data.learnersStarted > 0 && (
        <p className="notice notice--warn">
          {data.learnersStarted} {data.learnersStarted === 1 ? 'person has' : 'people have'} already worked on this unit.
          Changes show up for them straight away.
        </p>
      )}

      {note && <p className="notice">{note}</p>}
      {error && <p className="auth__error">{error}</p>}

      <section className="panel form">
        <label><span>Name</span>
          <input defaultValue={u.name} onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== u.name) void save({ name: v }, 'Renamed.'); }} />
        </label>
      </section>

      {!['test', 'survey', 'assignment', 'ilt', 'section'].includes(u.type) && <CompletionEditor key={u.id + JSON.stringify(u.completionParams) + u.completionType} unit={u} busy={busy} save={save} />}
      <UnitRulesEditor key={u.id + JSON.stringify(u.settings) + JSON.stringify(u.availability)} unit={u} busy={busy} save={save} />
      <Body unit={u} courseId={Number(id)} save={save} busy={busy} onError={setError} />

      {(u.type === 'test' || u.type === 'survey') && <p><Link className="btn btn--ghost" to={`${base}/${id}/edit/units/${unitId}/question-bank`}>Reuse questions and manage tags</Link></p>}
      {u.type === 'test' && <p><Link className="btn btn--ghost" to={`${base}/${id}/edit/units/${unitId}/question-import`}>Import questions</Link></p>}
      {(u.type === 'test' || u.type === 'survey') && (
        <Questions courseId={Number(id)} unitId={u.id} questions={u.questions ?? []}
          submittedAttempts={data.submittedAttempts} isSurvey={u.type === 'survey'}
          settings={u.settings} onChanged={load} onError={setError} onNote={setNote} />
      )}

      <p><button className="btn btn--ghost" onClick={() => navigate(`${base}/${id}/edit`)}>Back to the course</button></p>
    </>
  );
}

/* ───────────────────────────── per-type editors ───────────────────────────── */

function Body({ unit, courseId, save, busy, onError }: {
  unit: BuilderUnit['unit']; courseId: number;
  save: (b: Record<string, unknown>, m?: string) => Promise<void>;
  busy: boolean; onError: (m: string) => void;
}) {
  void courseId;
  switch (unit.type) {
    case 'section':
      return <p className="muted">A section heading just splits the list. It has no content of its own.</p>;

    case 'content':
      return (
        <section className="panel">
          <h2 className="h2">Lesson text</h2>
          <p className="muted small">HTML is allowed — headings, paragraphs, lists, images and links.</p>
          <HtmlBox initial={unit.contentHtml ?? ''} busy={busy} onSave={(html) => save({ contentHtml: html }, 'Lesson saved.')} />
        </section>
      );

    case 'video':
    case 'audio':
      return (
        <section className="panel">
          <h2 className="h2">{unit.type === 'video' ? 'Video' : 'Audio'}</h2>
          <MediaSource unit={unit} busy={busy} save={save} onError={onError}
            accept={unit.type === 'video' ? 'video/mp4,video/webm,video/quicktime' : 'audio/mpeg,audio/mp4,audio/wav'} />
          <h3 className="h2">Text below the player</h3>
          <HtmlBox initial={unit.secondaryHtml ?? ''} busy={busy} onSave={(html) => save({ secondaryHtml: html }, 'Text saved.')} />
        </section>
      );

    case 'document':
      return (
        <section className="panel">
          <h2 className="h2">Document</h2>
          <MediaSource unit={unit} busy={busy} save={save} onError={onError} accept=".pdf,.docx,.pptx,.xlsx" hideLink />
        </section>
      );

    case 'webpage':
      return (
        <section className="panel">
          <h2 className="h2">Web page</h2>
          <p className="muted small">The page is shown inside the course. Some sites refuse to be embedded — check it after saving.</p>
          <LinkBox initial={unit.url ?? ''} busy={busy} onSave={(url) => save({ url }, 'Link saved.')} />
        </section>
      );

    case 'assignment':
      return (
        <section className="panel">
          <h2 className="h2">What the trainee has to do</h2>
          <HtmlBox initial={unit.contentHtml ?? ''} busy={busy} onSave={(html) => save({ contentHtml: html }, 'Instructions saved.')} />
          <p className="muted small">Learners hand in text or a file. Instructors mark the submission in Learnica.</p>
        </section>
      );

    case 'craft':
      return <CraftEditor courseId={courseId} unitId={unit.id} />;

    case 'ilt':
      return <ClassSchedule courseId={courseId} unitId={unit.id} />;

    default:
      return null;
  }
}

function HtmlBox({ initial, busy, onSave }: { initial: string; busy: boolean; onSave: (html: string) => void }) {
  const [value, setValue] = useState(initial);
  const [preview, setPreview] = useState(false);
  useEffect(() => { setValue(initial); }, [initial]);
  const dirty = value !== initial;

  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span className="muted small">{dirty ? 'Unsaved changes' : 'Saved'}</span>
        <button className="btn btn--ghost" type="button" onClick={() => setPreview(!preview)}>
          {preview ? 'Edit' : 'Preview'}
        </button>
      </div>
      {preview
        ? <div className="prose preview" dangerouslySetInnerHTML={{ __html: value }} />
        : <textarea className="code" rows={14} value={value} onChange={(e) => setValue(e.target.value)} spellCheck />}
      <div className="row">
        <button className="btn btn--primary" disabled={busy || !dirty} onClick={() => onSave(value)}>Save</button>
        {dirty && <button className="btn btn--ghost" onClick={() => setValue(initial)}>Discard</button>}
      </div>
    </>
  );
}

function LinkBox({ initial, busy, onSave }: { initial: string; busy: boolean; onSave: (url: string) => void }) {
  const [value, setValue] = useState(initial);
  useEffect(() => { setValue(initial); }, [initial]);
  const ok = !value || /^https?:\/\//i.test(value);
  return (
    <div className="form">
      <label><span>Address</span>
        <input value={value} placeholder="https://…" onChange={(e) => setValue(e.target.value)} /></label>
      {!ok && <p className="auth__error">A link has to start with http:// or https://</p>}
      <div className="row">
        <button className="btn btn--primary" disabled={busy || !ok || value === initial} onClick={() => onSave(value)}>Save</button>
      </div>
    </div>
  );
}

/** Upload a file, or point at a YouTube link. */
function MediaSource({ unit, busy, save, onError, accept, hideLink }: {
  unit: BuilderUnit['unit']; busy: boolean;
  save: (b: Record<string, unknown>, m?: string) => Promise<void>;
  onError: (m: string) => void; accept: string; hideLink?: boolean;
}) {
  const [pct, setPct] = useState<number | null>(null);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setPct(0);
    try {
      const stored: UploadedFile = await uploadFile(file, setPct, unit.courseId);
      await save({ mediaFileId: stored.id, url: `/media/${stored.storagePath}` }, `"${stored.originalName}" uploaded.`);
    } catch (e) {
      onError((e as ApiError).message);
    } finally { setPct(null); }
  };

  const isEmbed = unit.url && /^https?:\/\//i.test(unit.url);

  return (
    <div className="form">
      {unit.url && (
        <p className="muted small">
          Currently: {isEmbed ? <a href={unit.previewUrl ?? unit.url} target="_blank" rel="noreferrer noopener">{unit.url}</a> : 'an uploaded file'}
        </p>
      )}

      <label><span>Upload a file</span>
        <input type="file" accept={accept} disabled={busy || pct !== null}
          onChange={(e) => void pick(e.target.files?.[0])} /></label>

      {pct !== null && (
        <>
          <div className="bar"><span style={{ width: `${pct}%` }} /></div>
          <span className="muted small">{pct}% — leave this page open until it finishes</span>
        </>
      )}

      {!hideLink && (
        <>
          <p className="muted small">…or use a YouTube link instead:</p>
          <LinkBox initial={isEmbed ? unit.url! : ''} busy={busy}
            onSave={(url) => save({ url, mediaFileId: null }, 'Link saved.')} />
        </>
      )}
    </div>
  );
}

/* ───────────────────────────── questions ───────────────────────────── */

function Questions({ courseId, unitId, questions, submittedAttempts, isSurvey, settings, onChanged, onError, onNote }: {
  courseId: number; unitId: number; questions: BuilderQuestion[];
  submittedAttempts: number; isSurvey: boolean; settings: Record<string, any>;
  onChanged: () => Promise<void>; onError: (m: string) => void; onNote: (m: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const passMark = settings?.test?.completion_threshold ?? 50;

  const guard = (what: string) => {
    if (submittedAttempts === 0) return true;
    return confirm(
      `This test has been sat ${submittedAttempts} ${submittedAttempts === 1 ? 'time' : 'times'}.\n\n` +
      `${what} applies to future attempts. Existing attempts keep their saved questions and marks.\n\nContinue?`,
    );
  };

  const force = submittedAttempts > 0 ? '?force=true' : '';

  return (
    <section className="panel">
      <div className="page-head">
        <h2 className="h2">Questions ({questions.length})</h2>
        <button className="btn btn--primary" onClick={() => setEditing('new')}>Add a question</button>
      </div>

      {!isSurvey && (
        <div className="row">
          <label><span>Pass mark</span>
            <input type="number" min={0} max={100} defaultValue={passMark} style={{ width: 90 }}
              onBlur={async (e) => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v) || v === passMark) return;
                setBusy(true);
                try {
                  await api.patch(`/admin/builder/${courseId}/units/${unitId}`, { settings: { test: { ...(settings?.test ?? {}), completion_threshold: v } } });
                  onNote(`Pass mark set to ${v}%.`); await onChanged();
                } catch (err) { onError((err as ApiError).message); } finally { setBusy(false); }
              }} />
          </label>
          <span className="muted small">Trainees need this percentage to pass.</span>
        </div>
      )}

      {submittedAttempts > 0 && (
        <p className="notice notice--warn">
          Sat {submittedAttempts} {submittedAttempts === 1 ? 'time' : 'times'} already. Changing the questions or
          answers will not re-mark anyone — past results stand as given.
        </p>
      )}

      {editing === 'new' && (
        <QuestionForm courseId={courseId} unitId={unitId} question={null} force={force} isSurvey={isSurvey}
          onDone={async (m) => { setEditing(null); onNote(m); await onChanged(); }}
          onCancel={() => setEditing(null)} onError={onError} guard={guard} />
      )}

      <ol className="qlist">
        {questions.map((q) => (
          <li key={q.id}>
            {editing === q.id ? (
              <QuestionForm courseId={courseId} unitId={unitId} question={q} force={force} isSurvey={isSurvey}
                onDone={async (m) => { setEditing(null); onNote(m); await onChanged(); }}
                onCancel={() => setEditing(null)} onError={onError} guard={guard} />
            ) : (
              <>
                <div className="prose" dangerouslySetInnerHTML={{ __html: q.textHtml }} />
                <p className="muted small">
                  {q.type.replace(/_/g, ' ')}
                  {q.answers?.possible ? ` · ${q.answers.possible.length} options` : ''}
                  {q.weight !== 1 ? ` · worth ${q.weight}` : ''}
                </p>
                <div className="row">
                  <button className="btn btn--ghost" onClick={() => setEditing(q.id)}>Edit</button>
                  <button className="link-danger" disabled={busy} onClick={async () => {
                    if (!guard('Removing a question')) return;
                    if (submittedAttempts === 0 && !confirm('Delete this question?')) return;
                    setBusy(true);
                    try {
                      await api.del(`/admin/builder/${courseId}/units/${unitId}/questions/${q.id}${force}`);
                      onNote('Question removed.'); await onChanged();
                    } catch (e) { onError((e as ApiError).message); } finally { setBusy(false); }
                  }}>Delete</button>
                </div>
              </>
            )}
          </li>
        ))}
      </ol>

      {questions.length === 0 && editing !== 'new' && <p className="muted">No questions yet.</p>}
    </section>
  );
}
